import { type Component, type JSX, Show } from "solid-js";
import { cn } from "@comandero/shared";
import { useTheme } from "../providers/theme-provider";

const DEFAULT_PATTERN_OPACITY = 10;

interface AppBackgroundProps {
  children?: JSX.Element;
  class?: string;
  isBlur?: boolean;
  glow0?: string;
  glow1?: string;
  glow2?: string;
  background?: string;
  patternType?: string;
  patternOpacity?: number;
}

const Patterns: Component<{ type: string; color: string; opacity: number }> = (props) => (
  <Show when={props.type !== "none"}>
    <div class="pointer-events-none absolute inset-0 overflow-hidden">
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <Show when={props.type === "dots"}>
            <pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1" fill={props.color} fill-opacity={props.opacity / 100} />
            </pattern>
          </Show>
          <Show when={props.type === "grid"}>
            <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
              <path
                d="M 30 0 L 0 0 0 30"
                fill="none"
                stroke={props.color}
                stroke-width="0.5"
                stroke-opacity={props.opacity / 100}
              />
            </pattern>
          </Show>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${props.type})`} />
      </svg>
    </div>
  </Show>
);

const FallbackGlows: Component<{ glow0?: string; glow1?: string; glow2?: string }> = (props) => (
  <div class="pointer-events-none absolute inset-0 overflow-hidden">
    {/* Top-Right Orb */}
    <div
      class="absolute top-[-200px] right-[-200px] w-[600px] h-[600px] opacity-[0.85] rounded-full transition-[background] duration-1000 ease-in-out"
      style={{
        background: `linear-gradient(to bottom, ${props.glow0 ?? "var(--glow-0)"}, transparent)`,
      }}
    />
    {/* Mid-Left Orb */}
    <div
      class="absolute top-[30%] left-[-40%] w-[900px] h-[900px] opacity-[0.75] rounded-full transition-[background] duration-1000 ease-in-out"
      style={{
        background: `linear-gradient(to bottom, ${props.glow1 ?? "var(--glow-1)"}, transparent)`,
      }}
    />
    {/* Bottom-Left Orb */}
    <div
      class="absolute bottom-[-150px] left-[-200px] w-[700px] h-[700px] opacity-[0.7] rounded-full transition-[background] duration-1000 ease-in-out"
      style={{
        background: `linear-gradient(to bottom, ${props.glow2 ?? "var(--glow-2)"}, transparent)`,
      }}
    />
  </div>
);

const GradientOrbs: Component<{
  isBlur: boolean;
  glow0?: string;
  glow1?: string;
  glow2?: string;
}> = (props) => (
  <div
    class="pointer-events-none absolute inset-[-100px]"
    style={{
      filter: props.isBlur ? "blur(60px)" : "none",
      opacity: "0.45",
    }}
  >
    {/* Orb 1: 100% 0% (Matches Native grad1) */}
    <div
      class="absolute h-[70%] w-[70%] rounded-full transition-transform duration-1000 ease-in-out will-change-transform"
      style={{
        top: "0%",
        left: "100%",
        transform: "translate(-50%, -50%)",
        background: props.isBlur
          ? `radial-gradient(circle, ${props.glow0 ?? "var(--glow-0)"} 0%, transparent 70%)`
          : `radial-gradient(circle at 100% 0%, ${props.glow0 ?? "var(--glow-0)"} 0%, ${props.glow0 ?? "var(--glow-0)"} 59.5%, transparent 60%)`,
      }}
    />

    {/* Orb 2: 0% 40% (Matches Native grad2) */}
    <div
      class="absolute h-[80%] w-[80%] rounded-full transition-transform duration-1000 ease-in-out will-change-transform"
      style={{
        top: "40%",
        left: "0%",
        transform: "translate(-50%, -50%)",
        background: props.isBlur
          ? `radial-gradient(circle, ${props.glow1 ?? "var(--glow-1)"} 0%, transparent 70%)`
          : `radial-gradient(circle at 0% 40%, ${props.glow1 ?? "var(--glow-1)"} 0%, ${props.glow1 ?? "var(--glow-1)"} 69.5%, transparent 70%)`,
      }}
    />

    {/* Orb 3: 10% 100% (Matches Native grad3) */}
    <div
      class="absolute h-[70%] w-[70%] rounded-full transition-transform duration-1000 ease-in-out will-change-transform"
      style={{
        top: "100%",
        left: "10%",
        transform: "translate(-50%, -50%)",
        background: props.isBlur
          ? `radial-gradient(circle, ${props.glow2 ?? "var(--glow-2)"} 0%, transparent 70%)`
          : `radial-gradient(circle at 10% 100%, ${props.glow2 ?? "var(--glow-2)"} 0%, ${props.glow2 ?? "var(--glow-2)"} 59.5%, transparent 60%)`,
      }}
    />

    {/* Orb 4: 80% 80% (Matches Native grad4) */}
    <div
      class="absolute h-[50%] w-[50%] rounded-full transition-transform duration-1000 ease-in-out will-change-transform"
      style={{
        top: "80%",
        left: "80%",
        transform: "translate(-50%, -50%)",
        background: props.isBlur
          ? `radial-gradient(circle, ${props.glow1 ?? "var(--glow-1)"} 0%, transparent 70%)`
          : `radial-gradient(circle at 80% 80%, ${props.glow1 ?? "var(--glow-1)"} 0%, ${props.glow1 ?? "var(--glow-1)"} 39.5%, transparent 40%)`,
      }}
    />
  </div>
);

const AtmosphericGlows: Component<{
  backgroundStyle: string;
  isBlur: boolean;
  glow0?: string;
  glow1?: string;
  glow2?: string;
}> = (props) => (
  <Show
    when={props.backgroundStyle === "gradients"}
    fallback={<FallbackGlows glow0={props.glow0} glow1={props.glow1} glow2={props.glow2} />}
  >
    <GradientOrbs
      isBlur={props.isBlur}
      glow0={props.glow0}
      glow1={props.glow1}
      glow2={props.glow2}
    />
  </Show>
);

/**
 * High-performance application background with atmospheric glows.
 * Matches the visual design and positioning of the native application (apps/native)
 * but optimized for web performance using GPU-accelerated layers.
 */
export const AppBackground: Component<AppBackgroundProps> = (props) => {
  const blur = () => props.isBlur ?? true;
  const theme = useTheme();

  const patternType = () => {
    if (props.patternType && props.patternType !== "none") {
      return props.patternType;
    }
    const tokenPattern = theme.tokens()?.patternType;
    if (tokenPattern && tokenPattern !== "none") {
      return tokenPattern;
    }
    return theme.backgroundStyle() === "lines" ? "grid" : "none";
  };

  const patternOpacity = () => {
    if (props.patternOpacity !== undefined) {
      return props.patternOpacity;
    }
    return theme.tokens()?.patternOpacity ?? DEFAULT_PATTERN_OPACITY;
  };

  return (
    <div
      class="fixed inset-0 overflow-hidden"
      style={{ "background-color": props.background ?? "var(--background)" }}
    >
      {/* ── Atmospheric Glows ────────────────────────────────────────────────── */}
      <AtmosphericGlows
        backgroundStyle={theme.backgroundStyle()}
        isBlur={blur()}
        glow0={props.glow0}
        glow1={props.glow1}
        glow2={props.glow2}
      />

      {/* ── Textural Patterns ────────────────────────────────────────────────── */}
      <Patterns type={patternType()} color="var(--foreground)" opacity={patternOpacity()} />

      {/* ── Content Layer ────────────────────────────────────────────────────── */}
      <div class={cn("relative z-10 h-full w-full", props.class)}>{props.children}</div>
    </div>
  );
};
